import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MagnifyingGlass, GraduationCap, Printer, FilePdf, CheckCircle, XCircle, CalendarBlank } from '@phosphor-icons/react';
import { publicApi, formatApiErrorDetail } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse, isValid } from 'date-fns';

const searchSchema = z.object({
  registrationNumber: z.string().min(1, 'Registration number is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
});

export default function PublicResultsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(searchSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await publicApi.searchResult(data);
      setResult(response.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date) {
      setValue('dateOfBirth', format(date, 'yyyy-MM-dd'));
      setDateInput(format(date, 'dd/MM/yyyy'));
    }
    setCalendarOpen(false);
  };

  const handleDateInputChange = (e) => {
    let value = e.target.value;
    
    // Remove non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes
    if (value.length === 2 && dateInput.length === 1) {
      value += '/';
    } else if (value.length === 5 && dateInput.length === 4) {
      value += '/';
    }
    
    // Limit to 10 characters (DD/MM/YYYY)
    if (value.length <= 10) {
      setDateInput(value);
      
      // Try to parse the date if complete
      if (value.length === 10) {
        const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
        if (isValid(parsedDate)) {
          setSelectedDate(parsedDate);
          setValue('dateOfBirth', format(parsedDate, 'yyyy-MM-dd'));
        }
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#E31E25] text-white py-3 px-4 sm:py-4 sm:px-6 no-print">
        <div className="max-w-7xl mx-auto flex items-center gap-3 sm:gap-4">
          <GraduationCap size={32} className="sm:w-10 sm:h-10 flex-shrink-0" weight="duotone" />
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold tracking-tight truncate" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Sasurie College of Arts and Science
            </h1>
            <p className="text-xs sm:text-sm text-white/80">(Autonomous) Vijayamangalam, Tirupur - Examination Results</p>
          </div>
        </div>
      </header>

      {!result ? (
        /* Search Form - Split Screen Layout */
        <div className="hero-split">
          {/* Form Side */}
          <div className="hero-form bg-white">
            <div className="max-w-md mx-auto lg:mx-0 w-full">
              <p className="overline mb-3 sm:mb-4">Student Results</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-[#1A1A1A] mb-4 sm:mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Check Your Results
              </h2>
              <p className="text-sm sm:text-base text-[#666666] mb-6 sm:mb-8 leading-relaxed">
                Enter your details below to view your examination results. 
                Please ensure all information matches your enrollment records.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <Label htmlFor="registrationNumber" className="text-sm font-semibold text-[#1A1A1A]">
                    Registration Number
                  </Label>
                  <Input
                    id="registrationNumber"
                    data-testid="registration-number-input"
                    {...register('registrationNumber')}
                    className="mt-2 rounded-none border-[#E8E8E8] focus:border-[#E31E25] focus:ring-[#E31E25]"
                    placeholder="Enter your registration number"
                  />
                  {errors.registrationNumber && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.registrationNumber.message}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#1A1A1A]">
                    Date of Birth
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      type="text"
                      data-testid="date-of-birth-input"
                      value={dateInput}
                      onChange={handleDateInputChange}
                      className="rounded-none border-[#E8E8E8] focus:border-[#E31E25] focus:ring-[#E31E25] pr-10"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="date-of-birth-calendar"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#E31E25] transition-colors"
                        >
                          <CalendarBlank size={20} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-none" align="end">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          captionLayout="dropdown-buttons"
                          fromYear={1980}
                          toYear={2010}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-[#666666] mt-1">Enter in DD/MM/YYYY format or use calendar</p>
                  <input type="hidden" {...register('dateOfBirth')} />
                  {errors.dateOfBirth && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.dateOfBirth.message}</p>
                  )}
                </div>

                {error && (
                  <div className="bg-[#E31E25]/10 border border-[#E31E25]/20 p-4 text-[#E31E25] text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="view-result-btn"
                  disabled={loading}
                  className="w-full bg-[#E31E25] hover:bg-[#C41A20] text-white py-6 rounded-none font-semibold text-base"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Searching...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <MagnifyingGlass size={20} weight="bold" />
                      View Result
                    </div>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Image Side */}
          <div 
            className="hero-image"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/207692/pexels-photo-207692.jpeg)',
            }}
          >
            <div className="absolute inset-0 bg-[#E31E25]/20" />
          </div>
        </div>
      ) : (
        /* Results Display */
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6 sm:mb-8 no-print">
            <Button
              onClick={() => setResult(null)}
              variant="outline"
              data-testid="search-again-btn"
              className="rounded-none border-[#E8E8E8] w-full sm:w-auto"
            >
              Search Again
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={handlePrint}
                data-testid="print-result-btn"
                className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none flex-1 sm:flex-none"
              >
                <Printer size={18} className="mr-2" />
                Print Result
              </Button>
            </div>
          </div>

          {/* Result Card */}
          <div className="result-watermark border border-[#E8E8E8] bg-white">
            {/* College Header for Print */}
            <div className="bg-[#E31E25] text-white p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                <GraduationCap size={28} className="sm:w-8 sm:h-8" weight="duotone" />
                <h1 className="text-lg sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Sasurie College of Arts and Science
                </h1>
              </div>
              <p className="text-white/90 text-xs sm:text-sm">(Autonomous) Vijayamangalam, Tirupur</p>
              <p className="text-white/80 text-xs sm:text-sm mt-1">Examination Results - Academic Year 2025-26</p>
            </div>

            {/* Student Info */}
            <div className="p-4 sm:p-6 border-b border-[#E8E8E8]">
              <h3 className="overline mb-3 sm:mb-4">Student Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="text-sm text-[#666666]">Student Name</p>
                  <p className="font-semibold text-[#1A1A1A]" data-testid="student-name">{result.student.studentName}</p>
                </div>
                <div>
                  <p className="text-sm text-[#666666]">Registration Number</p>
                  <p className="font-semibold text-[#1A1A1A]" data-testid="student-reg">{result.student.registrationNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-[#666666]">Program</p>
                  <p className="font-semibold text-[#1A1A1A]" data-testid="student-program">
                    {result.student.programName} ({result.student.programCode})
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#666666]">Date of Birth</p>
                  <p className="font-semibold text-[#1A1A1A]" data-testid="student-dob">{result.student.dateOfBirth}</p>
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="p-4 sm:p-6">
              <h3 className="overline mb-3 sm:mb-4">Examination Results</h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full min-w-[600px]" data-testid="results-table">
                  <thead>
                    <tr className="bg-[#FEF7F7] border-b border-[#E8E8E8]">
                      <th className="text-left py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">Subject Code</th>
                      <th className="text-left py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">Subject Name</th>
                      <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">Internal</th>
                      <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">External</th>
                      <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">Total</th>
                      <th className="text-center py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-[#1A1A1A]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((r, idx) => (
                      <tr key={idx} className="border-b border-[#E8E8E8] hover:bg-[#FEF7F7] transition-colors">
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium">{r.subjectCode}</td>
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm">{r.subjectName || '-'}</td>
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm text-right font-medium">
                          {r.internalMarks === 'AB' ? <span className="text-orange-600 font-semibold">AB</span> :
                           r.internalMarks === 'NF' ? <span className="text-purple-600 font-semibold">NF</span> :
                           r.internalMarks}
                        </td>
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm text-right font-medium">
                          {r.externalMarks === 'AB' ? <span className="text-orange-600 font-semibold">AB</span> :
                           r.externalMarks === 'NF' ? <span className="text-purple-600 font-semibold">NF</span> :
                           r.externalMarks}
                        </td>
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm text-right font-bold">{r.totalMarks}</td>
                        <td className="py-2 sm:py-3 px-3 sm:px-4 text-center">
                          {r.resultStatus === 'Pass' ? (
                            <span className="badge-pass" data-testid={`status-${idx}`}>
                              <CheckCircle size={14} weight="bold" />
                              Pass
                            </span>
                          ) : r.resultStatus === 'Absent' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-orange-50 text-orange-700 rounded-full border border-orange-200" data-testid={`status-${idx}`}>
                              Absent
                            </span>
                          ) : r.resultStatus === 'Not Found' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-purple-50 text-purple-700 rounded-full border border-purple-200" data-testid={`status-${idx}`}>
                              Not Found
                            </span>
                          ) : (
                            <span className="badge-fail" data-testid={`status-${idx}`}>
                              <XCircle size={14} weight="bold" />
                              Fail
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="mt-6 pt-4 border-t border-[#E8E8E8] flex justify-between items-center">
                <div>
                  <p className="text-sm text-[#666666]">Total Subjects: {result.results.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#666666]">
                    Overall: {result.results.filter(r => r.resultStatus === 'Pass').length}/{result.results.length} Passed
                    {result.results.some(r => r.resultStatus === 'Absent') && 
                      ` | ${result.results.filter(r => r.resultStatus === 'Absent').length} Absent`}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-[#FEF7F7] p-4 text-center border-t border-[#E8E8E8]">
              <p className="text-xs text-[#666666]">
                This is a computer-generated result. For official purposes, please obtain a verified copy from the college office.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
